/**
 * API Cache Service
 * Provides caching for API responses to improve performance
 * 
 * Features:
 * - In-memory caching with TTL (Time To Live)
 * - Automatic cache invalidation
 * - Request deduplication (prevents multiple identical requests)
 */

class ApiCache {
  constructor() {
    this.cache = new Map();
    this.pendingRequests = new Map();
    this.defaultTTL = 5 * 60 * 1000; // 5 minutes default
  }

  /**
   * Generate a cache key from URL and params
   */
  generateKey(url, params = {}) {
    const paramString = Object.keys(params).length > 0 
      ? JSON.stringify(params) 
      : '';
    return `${url}${paramString}`;
  }

  /**
   * Get cached data if valid
   */
  get(key) {
    const cached = this.cache.get(key);
    if (!cached) return null;
    
    if (Date.now() > cached.expiry) {
      this.cache.delete(key);
      return null;
    }
    
    return cached.data;
  }

  /**
   * Set cache data with TTL
   */
  set(key, data, ttl = this.defaultTTL) {
    this.cache.set(key, {
      data,
      expiry: Date.now() + ttl,
      timestamp: Date.now()
    });
  }

  /**
   * Invalidate cache by key or pattern
   */
  invalidate(keyOrPattern) {
    if (typeof keyOrPattern === 'string') {
      // Exact match
      this.cache.delete(keyOrPattern);
    } else if (keyOrPattern instanceof RegExp) {
      // Pattern match
      for (const key of this.cache.keys()) {
        if (keyOrPattern.test(key)) {
          this.cache.delete(key);
        }
      }
    }
  }

  /**
   * Invalidate all cache entries containing a string
   */
  invalidateContaining(substring) {
    for (const key of this.cache.keys()) {
      if (key.includes(substring)) {
        this.cache.delete(key);
      }
    }
  }

  /**
   * Clear all cache
   */
  clear() {
    this.cache.clear();
    this.pendingRequests.clear();
  }

  /**
   * Get cache stats
   */
  getStats() {
    return {
      size: this.cache.size,
      keys: Array.from(this.cache.keys()),
      pendingRequests: this.pendingRequests.size
    };
  }
}

// Singleton instance
const apiCache = new ApiCache();

/**
 * Cached fetch wrapper
 * @param {Function} fetchFn - The fetch function to call
 * @param {string} cacheKey - Unique cache key
 * @param {number} ttl - Time to live in ms (default: 5 minutes)
 * @param {boolean} forceRefresh - Force a fresh fetch
 */
export async function cachedFetch(fetchFn, cacheKey, ttl = 5 * 60 * 1000, forceRefresh = false) {
  // Check cache first
  if (!forceRefresh) {
    const cached = apiCache.get(cacheKey);
    if (cached) {
      return cached;
    }
  }

  // Check if there's already a pending request for this key
  if (apiCache.pendingRequests.has(cacheKey)) {
    return apiCache.pendingRequests.get(cacheKey);
  }

  // Create new request
  const requestPromise = fetchFn()
    .then(data => {
      apiCache.set(cacheKey, data, ttl);
      apiCache.pendingRequests.delete(cacheKey);
      return data;
    })
    .catch(error => {
      apiCache.pendingRequests.delete(cacheKey);
      throw error;
    });

  apiCache.pendingRequests.set(cacheKey, requestPromise);
  return requestPromise;
}

/**
 * Invalidate cache for specific resource types
 */
export const invalidateCache = {
  // Invalidate all customer-related cache
  customer: () => apiCache.invalidateContaining('/portal/'),
  
  // Invalidate nutrition data
  nutrition: () => apiCache.invalidateContaining('/nutrition/'),
  
  // Invalidate appointments
  appointments: () => apiCache.invalidateContaining('/appointments'),
  
  // Invalidate packages/points
  packages: () => apiCache.invalidateContaining('/packages'),
  
  // Invalidate invoices
  invoices: () => apiCache.invalidateContaining('/invoices'),
  
  // Invalidate all
  all: () => apiCache.clear(),
  
  // Invalidate specific key
  key: (key) => apiCache.invalidate(key)
};

/**
 * Cache TTL presets (in milliseconds)
 */
export const CacheTTL = {
  SHORT: 1 * 60 * 1000,      // 1 minute - for frequently changing data
  MEDIUM: 5 * 60 * 1000,     // 5 minutes - default
  LONG: 15 * 60 * 1000,      // 15 minutes - for rarely changing data
  VERY_LONG: 60 * 60 * 1000, // 1 hour - for static data
};

export default apiCache;
